import { ObjectTypeLabelAndKeyTag } from '../../common/ObjectTypeLabelAndKey';
import { Box, Divider, Paper } from '@mui/material';
import {
  IconButton,
  Tooltip,
  Typography,
  __SWEEP_DESIGN_TYPOGRAPHY_STYLES___,
  colors,
} from '@sweep-io/sweep-design';
import { RfNodeGroupLabel } from '../canvas-types/nodeTypesData';
import { PluginTypes } from '../../../types/enums/PluginTypes';
import { getPluginButtonId } from '../pluginHelpers';
import {
  PluginDataManagementSvg,
  PluginBantSvg,
  PluginDynamicPathSvg,
  PluginMeddicSvg,
  PluginMeddpiccSvg,
} from '../../pages/funnel-map-flow/templates/plugins/icons';
import { styled } from '@mui/material/styles';
import { memo, useCallback, useRef } from 'react';
import { Pills } from './components/pills/Pills';
import { AggregatedPill } from './components/pills/AggregatedPill';
import { NodeEntityTypes } from '../canvas-types';
import { CONTEXT_ZOOM_DEFAULT_CLASS_NAME } from './contextZoomConsts';
import { getObjectTypeColor, objectTypeColorNames } from '../helpers/getObjectTypeColor';
import HubspotIcon from './svg/hubspot.svg?react';
import { LogoDevIcon } from '../../common/logo-dev-selector/LogoDevIcon';
import { capitalizeString } from '../../../lib/capitalizeFirstLetter';
import { NodeProps } from '@xyflow/react';
import { SweepNodeHandles } from './SweepNodeHandles';
import { ActionButtonConnectStep } from './components/CanvasButtons';
import FunnelAiAgentsButton from './components/FunnelAiAgentsButton';
import { CANVAS_COLOR_HIGHLIGHT } from '../const';
import classNames from 'classnames';
import { useNodeDimensionsObserver } from './useNodeDimensionsObserver';
import { createZoomTransformNodeClass, ZOOM_TRANSFORM_CLASSES } from './getZoomTransformStyle';
import { MoreActionsHorizontal } from '@sweep-io/sweep-design/dist/icons';

type PluginsDataType = {
  [key in PluginTypes]: {
    tooltip: string;
    icon: JSX.Element;
  };
};

const pluginsData: PluginsDataType = {
  [PluginTypes.DATA_MANAGEMENT]: {
    tooltip: 'Data Management',
    icon: <PluginDataManagementSvg />,
  },
  [PluginTypes.BANT]: {
    tooltip: 'BANT',
    icon: <PluginBantSvg />,
  },
  [PluginTypes.DYNAMIC_PATH]: {
    tooltip: 'Dynamic Path',
    icon: <PluginDynamicPathSvg />,
  },
  [PluginTypes.MEDDIC]: {
    tooltip: 'MEDDIC',
    icon: <PluginMeddicSvg />,
  },
  [PluginTypes.MEDDPICC]: {
    tooltip: 'MEDDIC',
    icon: <PluginMeddpiccSvg />,
  },
};

const PluginButton = styled(Box)`
  cursor: pointer;
  display: flex;
  border: 2px solid ${colors.white};
  border-radius: 4px;

  svg {
    width: 24px;
    height: 24px;
  }

  &:hover {
    opacity: 0.8;
    border-color: ${colors.blue[500]};
  }
`;

const getSweepLabelBackgroundColors = () =>
  objectTypeColorNames.reduce((acc, objectType) => {
    const color = getObjectTypeColor(objectType).step;
    return {
      ...acc,
      [`&.object-type-${objectType}`]: {
        backgroundColor: color,
      },
    };
  }, {});

export const groupLabelStyle = {
  '.sweep-group-label-node .group-label-bottom-row, .sweep-group-label-node .aggregated-pill': {
    display: 'none',
  },

  [`&.${CONTEXT_ZOOM_DEFAULT_CLASS_NAME}`]: {
    '.sweep-group-label-node .group-label-bottom-row': {
      display: 'block',
    },
    '.sweep-group-label-node .aggregated-pill': {
      display: 'none',
    },
  },
  [`&:not(.${CONTEXT_ZOOM_DEFAULT_CLASS_NAME})`]: {
    '.sweep-group-label-node .group-label-bottom-row': {
      display: 'none',
    },
    '.sweep-group-label-node .aggregated-pill': {
      display: 'block',
    },
    '.sweep-group-label-node .group-label-paper': {
      backgroundColor: getObjectTypeColor().step,
      ...getSweepLabelBackgroundColors(),
    },
    '.sweep-group-label-node .object-type-chip': {
      '&, span': {
        backgroundColor: '#fff',
      },
    },
    '.sweep-group-label-node .status-label': {
      display: 'none',
    },
    '.sweep-group-label-node .more-button': {
      display: 'none',
    },
  },
};

const GroupLabelNode = ({
  data,
  dragging,
  width,
  height,
  id,
  selected,
}: NodeProps<RfNodeGroupLabel>) => {
  const {
    hideGroupInfo,
    pills,
    description,
    label,
    objectType,
    objectLabel,
    labelPlugins,
    groupId,
    onLabelClick,
    startingIcon,
    onPillClick,
    objectLabelLogoDevBrand,
    isHighlighted,
    onConnectClick,
    showButtonsOnHighlight,
    isReorderingNodes,
    zoomTransformSuffix,
    status,
    moreButton,
  } = data;

  const wrapperRef = useNodeDimensionsObserver({ id, width, height });
  const parentWrapperRef = useRef<HTMLDivElement>(null);

  const onLabelClickHandler = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      onLabelClick?.({
        event,
        entity: { type: NodeEntityTypes.GroupLabel },
        id: groupId,
      });
    },
    [onLabelClick, groupId],
  );

  if (hideGroupInfo) {
    return <></>;
  }

  const showPills = Boolean(pills?.length) && !isReorderingNodes;
  const showBottomRow = showPills || description;
  const showAggregatedPills = false;

  const showClickableIcon = !!onLabelClick;
  const startIcon = objectLabelLogoDevBrand ? (
    <LogoDevIcon brand={objectLabelLogoDevBrand} size={16} quality="higher" />
  ) : undefined;

  const showButtons = showButtonsOnHighlight && isHighlighted && onConnectClick;

  const _onLabelClick = isReorderingNodes ? undefined : onLabelClickHandler;

  return (
    <Box
      data-testid="sweep-group-label-node"
      className={classNames('sweep-group-label-node', {
        'is-reordering-nodes': isReorderingNodes,
        [ZOOM_TRANSFORM_CLASSES.NODE]: true,
        [createZoomTransformNodeClass(zoomTransformSuffix)]: true,
      })}
      sx={{
        width,
        height,
        cursor: dragging ? 'move' : showClickableIcon ? 'pointer' : 'move',
      }}
      onClick={_onLabelClick}
    >
      <Box sx={{ position: 'absolute' }} ref={wrapperRef}>
        <SweepNodeHandles>
          <Box
            sx={{ transformOrigin: 'top left' }}
            className={ZOOM_TRANSFORM_CLASSES.TARGET}
            ref={parentWrapperRef}
          >
            {showButtons && (
              <Box
                sx={{
                  position: 'absolute',
                  right: '-41px',
                  top: 0,
                  cursor: 'default',
                }}
              >
                <ActionButtonConnectStep
                  onClick={(event) => onConnectClick(event, { groupId }, parentWrapperRef.current)}
                />
              </Box>
            )}
            <Paper
              elevation={1}
              sx={{
                borderRadius: '8px',
                border: '2px solid',
                borderColor: selected ? CANVAS_COLOR_HIGHLIGHT : 'transparent',
                transition: 'width .3s ease, background-color 0.5s ease',
                display: 'flex',
                flexDirection: 'column',
                padding: `12px 16px`,
                gap: '8px',
              }}
              className={`group-label-paper object-type-${objectType}`}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  width: 'auto',
                  '&:hover': {
                    borderColor: colors.blue[500],
                  },
                  '.object-type-chip div': {
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'center',
                  },
                }}
              >
                {startingIcon === 'hubspot' && <HubspotIcon />}
                <Typography variant="h3" whiteSpace="nowrap">
                  {label}
                </Typography>
                <ObjectTypeLabelAndKeyTag
                  objectType={objectType}
                  label={capitalizeString(objectLabel)}
                  startIcon={startIcon}
                  tagVariant="medium"
                  noObjectNameTooltip={['__hubspot__', '__third_party_funnel__'].includes(
                    objectType,
                  )}
                  disablePortal
                />
                {labelPlugins.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      width: 'auto',
                    }}
                  >
                    {labelPlugins.map((plugin) => (
                      <Tooltip
                        key={plugin.type}
                        title={`${pluginsData[plugin.type].tooltip} plugin`}
                        disablePortal
                      >
                        <PluginButton
                          id={getPluginButtonId(plugin.type, groupId)}
                          onClick={(e) => {
                            e.stopPropagation();
                            plugin.onClick?.(e);
                          }}
                        >
                          {pluginsData[plugin.type].icon}
                        </PluginButton>
                      </Tooltip>
                    ))}
                  </Box>
                )}
                <FunnelAiAgentsButton funnelId={data.groupId} />
                {showPills && showAggregatedPills && <AggregatedPill data={data} />}
                {status && (
                  <span className="status-label">
                    <Typography variant="body" color={colors.grey[700]} whiteSpace="nowrap">
                      {status}
                    </Typography>
                  </span>
                )}
                {moreButton?.display && (
                  <Box
                    sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                    className="more-button"
                  >
                    <Divider
                      dir="vertical"
                      sx={{ borderWidth: '1px', borderColor: colors.grey[120], height: '18px' }}
                    />
                    <IconButton
                      variant="squared"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onLabelClick?.({
                          event: e,
                          entity: { type: NodeEntityTypes.GroupMore },
                          id: groupId,
                        });
                      }}
                    >
                      <MoreActionsHorizontal />
                    </IconButton>
                  </Box>
                )}
              </Box>

              {showBottomRow && (
                <Box
                  className="group-label-bottom-row"
                  sx={{
                    borderTop: `1px solid ${colors.grey[120]}`,
                    paddingTop: '12px',
                    ...__SWEEP_DESIGN_TYPOGRAPHY_STYLES___.caption,
                    color: colors.grey[700],
                  }}
                >
                  {description && <>{description}</>}
                  {pills?.length && (
                    <Pills
                      nodeId={groupId}
                      nodeLabel={label}
                      objectType={objectType}
                      pills={pills}
                      onPillClick={onPillClick}
                      entityType={NodeEntityTypes.GroupPill}
                    />
                  )}
                </Box>
              )}
            </Paper>
          </Box>
        </SweepNodeHandles>
      </Box>
    </Box>
  );
};

const _memoGroupLabelNode = memo(GroupLabelNode);

export default _memoGroupLabelNode;
